import { captureException } from '@sentry/browser';
import {
  // eslint-disable-next-line no-restricted-imports
  GCInstant as DefaultPlatformInstance,
  PlatformWeb,
  type Platform,
  PlatformLineGuest,
  YahooAdsPlugin,
  isInLineMiniApp,
  type CommonAnalyticsV2,
  GCInstantError,
  EntryDataOverridePlugin,
} from '@play-co/gcinstant';
import { getLineConfig } from 'src/conf/env';
import { finishGameLoad } from 'src/lib/analytics/gameLoadTracking';
import type { EntryProperties } from 'src/lib/analytics/properties';

// gcinstant forwards arbitrary SplashHide properties, but v85.2.0 still types
// dismissLoadingScreenAsync analytics as CommonAnalyticsV2, whose feature and
// subFeature fields are required.
type SplashHideAnalytics = Record<string, unknown>;

export type WithEntryData<T extends Platform> = T & {
  entryData: EntryProperties;
  dismissLoadingScreenAsync(analytics?: SplashHideAnalytics): Promise<void>;
};

/**
 * Actual platform instance, can be PlatformWeb or PlatformLineGuest.
 * Import it only for tests never in production code!
 */
export let platformInstance =
  DefaultPlatformInstance as WithEntryData<Platform>;

/**
 * Yahoo Ads plugin instance, if registered.
 * We use it only in LINE Mini App environment. Otherwise it remains undefined.
 */
export let yahooAdsPlugin: YahooAdsPlugin | undefined = undefined;

/**
 * This is for simulating the splash screen in local offline development
 */
if (process.env.IS_LOCAL_DEVELOPMENT) {
  // Simulate the same splash screen we see on web and in native apps for local development.
  const originalFn = platformInstance.dismissLoadingScreenAsync;
  platformInstance.dismissLoadingScreenAsync = async (analytics) => {
    const splashScreen = document.querySelector('#splashScreen');
    splashScreen?.remove();
    await originalFn.call(platformInstance, analytics);
  };
  // Always consider subscribed in local dev, to avoid the force subscribe prompt
  platformInstance.isSubscribedToBot = true;
}

/**
 * Switch the platform instance to web platform. Must be called before initializing GCInstant.
 */
export function usePlatformWeb(): void {
  if (platformInstance.platformID !== 'web') {
    platformInstance = new PlatformWeb({
      legacyNativeInterface: DefaultPlatformInstance.legacyNative,
    }) as WithEntryData<PlatformWeb>;
  }
}

/**
 * Switch the platform instance to LINE Guest platform. Must be called before initializing GCInstant.
 */
export function usePlatformLineGuest() {
  if (platformInstance.platformID !== 'line-guest') {
    platformInstance = new PlatformLineGuest({
      legacyNativeInterface: DefaultPlatformInstance.legacyNative,
    }) as WithEntryData<PlatformLineGuest>;
  }
}

/**
 * Return the current GCInstant instance with the correct platform, see platformInstance
 * Use it for production code to access the platform methods and properties.
 */
export const GCInstant = new Proxy({} as WithEntryData<Platform>, {
  get(_target, prop, _receiver) {
    if (process.env.IS_DEVELOPMENT && prop === 'entryPointName') {
      const entryPointName = new URLSearchParams(window.location.search).get(
        'entryPointName',
      );

      if (entryPointName) {
        return entryPointName;
      }
    }

    if (prop === 'dismissLoadingScreenAsync') {
      // Every launch path dismisses the splash through here, so the game load
      // is reported from one place. Only the first call per session sends.
      return (splashHideAnalytics?: SplashHideAnalytics) => {
        const launchPath = splashHideAnalytics?.launchPath;
        finishGameLoad(typeof launchPath === 'string' ? launchPath : undefined);

        return platformInstance.dismissLoadingScreenAsync(splashHideAnalytics);
      };
    }

    const value = Reflect.get(platformInstance, prop, platformInstance);

    if (typeof value === 'function') {
      // Bind the function to the target instance to maintain `this` context
      return value.bind(platformInstance);
    }

    return value;
  },

  set(_target, prop, value, _receiver) {
    return Reflect.set(platformInstance, prop, value, platformInstance);
  },

  getPrototypeOf() {
    return Reflect.getPrototypeOf(platformInstance);
  },
});

// See https://support.appsflyer.com/hc/en-us/articles/360018941117-Recommended-gaming-app-events#level-achieved-af_level_achieved
export function trackAppsFlyerLevelAchieved(opts: {
  /** Level the user achieved. */
  level?: number;
  /** Score associated with user's achievement. */
  score?: number;
}) {
  GCInstant.legacyNative?.appsFlyer.logEvent('af_level_achieved', {
    af_level: opts.level,
    af_score: opts.score,
  });
}

// See https://support.appsflyer.com/hc/en-us/articles/360018941117-Recommended-gaming-app-events#tutorial-completion-af_tutorial_completion
export function trackAppsFlyerTutorialCompletion(opts: {
  /** Whether the user completed the tutorial */
  success?: boolean;
  /** Tutorial ID */
  tutorialId?: string;
  /** Tutorial name */
  content?: string;
}) {
  GCInstant.legacyNative?.appsFlyer.logEvent('af_tutorial_completion', {
    af_success: opts.success,
    af_tutorial_id: opts.tutorialId,
    af_content: opts.content,
  });
}
// See https://support.appsflyer.com/hc/en-us/articles/360018941117-Recommended-gaming-app-events#share-af_share
export function trackAppsFlyerShare(opts: {
  /** Reason for sharing on social media, for example, a new high score or leveling up. */
  description?: string;
  /** Platform used to post shares. */
  platform?: string;
}) {
  GCInstant.legacyNative?.appsFlyer.logEvent('af_share', {
    af_description: opts.description,
    platform: opts.platform,
  });
}

// See https://support.appsflyer.com/hc/en-us/articles/360018941117-Recommended-gaming-app-events#invite-af_invite
export function trackAppsFlyerInvite(opts: {
  /** Context of invitation. */
  description?: string;
}) {
  GCInstant.legacyNative?.appsFlyer.logEvent('af_invite', {
    af_description: opts.description,
  });
}

/**
 * Register Yahoo Ads plugin if in LINE Mini App environment
 *
 * @param platform
 * @returns YahooAdsPlugin
 */
export function registerYahooAdsPlugin(
  platform: WithEntryData<Platform>,
): YahooAdsPlugin {
  const yahoo = new YahooAdsPlugin();
  platform.addPlugin(yahoo);
  return yahoo;
}

/**
 * Register entry data override plugin to resolve short URL parameters (e.g., `?c=tiktok`)
 * into entry data overrides after GCInstant parses entry data.
 * Spec: https://docs.google.com/document/d/1X0zW2Gh76KkyKc91riCIhjH6Gb0Uf3nhU0W4jGQXmH8/edit?tab=t.0
 *
 */
function registerEntryDataOverridePlugin(
  platform: WithEntryData<Platform>,
): void {
  const namedPayloads: Record<string, Record<string, unknown>> = {
    tiktok: {
      $channel: 'PAGE',
      $pageLinkId: 'tiktok',
      $pageLinkType: 'tiktok_profile',
    },
    twitter: {
      $channel: 'PAGE',
      $pageLinkId: 'twitter',
      $pageLinkType: 'twitter_profile',
    },
    line: {
      $channel: 'PAGE',
      $pageLinkId: 'line',
      $pageLinkType: 'line_promotion',
    },
  };

  const plugin = new EntryDataOverridePlugin(() => {
    const linkId = new URLSearchParams(window.location.search).get('c');
    return linkId ? namedPayloads[linkId] : undefined;
  });

  platform.addPlugin(plugin);
}

/**
 * Delay host environment detection until after DOM content has loaded.
 *
 * **Resolve this function before accessing `GCInstant.legacyNative`.**
 *
 * This is to handle an issue in the Android native app (native-builder) where the `window.TLNative` property
 * injected by the native host is not always available by the time client code gets evaluated.
 */
export async function blockUntilHostIsReady(): Promise<void> {
  await blockUntilDOMContentLoaded();

  // The bootstrap imports this application alongside the platform SDK, so
  // `window.liff` is not guaranteed until this settles. Everything below reads
  // it (`usePlatformWeb`, `isInLineMiniApp`), as does launch code after it. A
  // rejection stays unhandled: entering LINE without the SDK means entering as
  // the wrong player.
  await window.__platformSDKReady;

  // Skip host detection in mock mode (local dev without LINE SDK)
  if (process.env.PLATFORM === 'mock') return;

  const { channel, channelId, env } = getLineConfig(
    DefaultPlatformInstance.legacyNative,
  );

  console.debug(`Detected host environment: ${env}`);
  console.debug(`Selected LINE channel: ${channel} (${channelId})`);

  switch (channel) {
    case 'login':
    case 'miniapp':
      usePlatformWeb();
      break;
  }

  // Register Yahoo Ads plugin for LINE Mini App (web platform)
  if (channel === 'miniapp' && !yahooAdsPlugin && isInLineMiniApp()) {
    yahooAdsPlugin = registerYahooAdsPlugin(platformInstance);
  }

  // Register entry data override plugin
  registerEntryDataOverridePlugin(platformInstance);
}

async function blockUntilDOMContentLoaded(): Promise<void> {
  await new Promise<void>((resolve) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => resolve());
    } else {
      resolve(); // `DOMContentLoaded` has already fired
    }
  });
}

/**
 * Request LINE Mini App Terms of Service consent agreement.
 *
 * Uses the GCInstant `requestMiniAppToSConsentAsync` API to trigger the LINE
 * Mini App IAP Terms of Service consent flow.
 *
 * @param analytics - Analytics properties for tracking (feature, subFeature)
 * @returns `true` if TOS was accepted, `false` if user rejected
 */
export async function requestMiniAppToSConsent(
  analytics: CommonAnalyticsV2,
): Promise<boolean> {
  // Only PlatformWeb has the requestMiniAppToSConsentAsync method
  if (!(platformInstance instanceof PlatformWeb)) {
    return false;
  }

  try {
    await platformInstance.requestMiniAppToSConsentAsync(analytics);
    return true;
  } catch (error) {
    // USER_INPUT error means user rejected the consent
    if (error instanceof GCInstantError && error.code === 'USER_INPUT') {
      return false;
    }
    // Re-throw other errors
    throw error;
  }
}

/**
 * Check if the LIFF friendship API (requestFriendship/getFriendship) is available.
 * Mirrors PlatformWeb's LIFF SDK readiness check, then verifies the user is
 * logged in with an access token before calling login-gated LIFF APIs.
 */
export function isLiffFriendshipApiSupported(): boolean {
  const liff = window.liff;

  try {
    // LIFF readiness accessors can throw before the SDK finishes initializing.
    // Treat those as unsupported here because this helper is a flow-selection
    // predicate, not the operation that should report request failures.
    return (
      platformInstance instanceof PlatformWeb &&
      !!liff?.id &&
      !!liff.isInClient?.() &&
      !platformInstance.legacyNative &&
      liff.isLoggedIn?.() === true &&
      !!liff.getAccessToken?.() &&
      typeof liff.getFriendship === 'function' &&
      typeof liff.requestFriendship === 'function'
    );
  } catch (error) {
    captureException(error, {
      tags: { context: 'isLiffFriendshipApiSupported' },
    });
    return false;
  }
}

/**
 * Request LINE Official Account friendship via the LIFF SDK.
 * Check {@link isLiffFriendshipApiSupported} before calling.
 * @throws If called when LIFF friendship API is not supported.
 */
export async function requestFriendshipAsync(): Promise<void> {
  if (!(platformInstance instanceof PlatformWeb)) {
    throw new Error('requestFriendshipAsync requires PlatformWeb');
  }

  await platformInstance.requestFriendshipAsync();
}

/**
 * Get LINE Official Account friendship status via the LIFF SDK.
 * Check {@link isLiffFriendshipApiSupported} before calling.
 * @returns `true` if user is friends with OA, `false` otherwise.
 * @throws If called when LIFF friendship API is not supported.
 */
export async function getFriendshipAsync(): Promise<boolean> {
  if (!(platformInstance instanceof PlatformWeb)) {
    throw new Error('getFriendshipAsync requires PlatformWeb');
  }

  const result = await platformInstance.getFriendshipAsync();
  return result ?? false;
}
